import config from '../startup/config';
import Storage from './Storage';
import Phaser from 'phaser';

type TrackName = 'sound' | 'music' | string;
type VolumeMap = Record<TrackName, number>;
type AudioMap = Record<TrackName, Phaser.Sound.WebAudioSound[]>;

export default class AudioManager {
    private scene!: Phaser.Scene;
    private storage!: Storage;
    private tracks: TrackName[] | null = null;
    private audio: AudioMap = {};
    private volume: VolumeMap = {};

    constructor(scene?: Phaser.Scene) {
        if (scene) {
            this.scene = scene;
            this.storage = (scene.game as any).storage; // расширим позже через global.d.ts
        }
    }

    /**
     * Sets volume for the supplied track
     *
     * @param {string} track
     * @param {Number} volume
     */
    setVolume(track: TrackName, volume: number): void {
        this.volume[track] = volume;
        this.audio[track]?.forEach((instance) => instance.setVolume(volume));
        this.storage.setItem(`volume_${track}`, volume);
    }

    /**
     * Returns volume for the supplied track
     *
     * @param {string} track
     * @returns {number}
     */
    getVolume(track: TrackName): number {
        return parseFloat(String(this.volume[track]));
    }

    /**
     * Mutes or enables volume for the supplied track
     * Volume is based on config props
     *
     * @param {*} track
     * @returns {number} new volume
     */
    flipVolume(track: TrackName): number {
        const currentVolume = this.getVolume(track);
        const maxVolume = (config as any)[`${track}MaxVolume`] ?? 1;
        const volume = currentVolume === 0 ? maxVolume : 0;

        this.setVolume(track, volume);
        return parseFloat(String(volume));
    }

    /**
     * Loads audiosprites for each requested track
     *
     * @param {Phaser.Scene} scene
     * @param {Array} tracks track names, e.g. ['sound', 'music']
     */
    load(scene: Phaser.Scene, tracks: TrackName[]): void {
        this.scene = scene;
        this.tracks = tracks;
        this.storage = (scene.game as any).storage;

        const path = 'assets/audio';
        const formats = ['.ogg', '.m4a', '.mp3', '.ac3'];

        scene.load.setPath(path);

        tracks.forEach((track) => {
            this.audio[track] = [];

            const sources = formats.map((format) => `${track}${format}?${Date.now()}`);
            scene.load.audioSprite(track, `${track}.json`, sources);
        });

        scene.load.setPath('');

        tracks.forEach((track) => {
            const stored = this.storage.initItem(`volume_${track}`, (config as any)[`${track}MaxVolume`] ?? 1);
            this.volume[track] = parseFloat(String(stored));
        });
    }

    /**
     * Plays the sound and returns sound instance
     *
     * @param {string} track eg 'sound' or 'music'
     * @param {string} name sound name
     * @param {Object} options
     * @returns {} instance
     */
    play(
        track: TrackName,
        name: string,
        options: Phaser.Types.Sound.SoundConfig = {}
    ): Phaser.Sound.WebAudioSound & { track: TrackName } {
        const instance = this.scene.sound.addAudioSprite(track) as Phaser.Sound.WebAudioSound & { track: TrackName };
        instance.track = track;
        this.audio[track].push(instance);

        instance.once('complete', () => this.remove(instance));

        options.volume = this.volume[track];
        instance.play(name, options);

        return instance;
    }

    /**
     * Removes the instance from the list and destroys it
     *
     * @param {*} instance
     */
    remove(instance: Phaser.Sound.WebAudioSound & { track: TrackName }): void {
        const track = instance.track;
        const list = this.audio[track];
        const index = list.indexOf(instance);
        if (index !== -1) list.splice(index, 1);

        if (instance.isPlaying) instance.destroy();
    }
}
